import React, { useCallback, useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { api } from '../../lib/api';
import FacilitatorPageHeader from './FacilitatorPageHeader';
import { ISSUE_TYPES } from '../questions/ChartAnnotation';

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB'); } catch { return iso; }
};

// Mirror server VERIFY_ACTION_LABELS - kept inline so this component
// doesn't depend on the student workspace file. Keep in sync with
// StudentOutpatientWorkspace.jsx.
const VERIFY_ACTION_LABELS = {
  sign_off: 'No errors found — sign off as safe',
  amend_myself: 'Amend prescription, sign and annotate',
  query_with_prescriber: 'Query with the prescriber',
  provide_advice_to_patient: 'Provide advice to the patient',
  refuse_to_dispense: 'Refuse to dispense',
  refer_back: 'Refer back to the prescriber',     // back-compat
};
const verifyActionLabel = (k) => VERIFY_ACTION_LABELS[k] || k || '—';

const SEVERITY_BADGE = (s) => {
  if (s === 'clinical') return { bg: 'danger' };
  if (s === 'legal') return { bg: 'warning', text: 'dark' };
  if (s === 'best_practice') return { bg: 'info' };
  return { bg: 'secondary' };
};

const PrescriptionDetail = ({ rx }) => {
  if (!rx) return null;
  const head = [rx.drug, rx.strength, rx.formulation].filter(Boolean).join(' ');
  const dose = rx.doseAmount != null && rx.doseAmount !== ''
    ? `${rx.doseAmount}${rx.doseUnit ? ` ${rx.doseUnit}` : ''}` : '';
  const route = rx.route || '';
  const duration = rx.durationDays
    ? `${rx.durationDays} day${rx.durationDays === 1 ? '' : 's'}`
    : (rx.durationDoses ? `${rx.durationDoses} dose${rx.durationDoses === 1 ? '' : 's'}` : '');
  const qty = rx.quantity != null && rx.quantity !== '' ? `Qty ${rx.quantity}` : '';
  return (
    <div className="small">
      <div className="fw-semibold">{head || '—'}</div>
      <div className="text-muted">
        {[dose, rx.frequency, route, duration, qty].filter(Boolean).join(' | ')}
      </div>
    </div>
  );
};

const PrescribeBreakdown = ({ session }) => {
  const grading = session.grading || {};
  const slotResults = Array.isArray(grading.slotResults) ? grading.slotResults : [];
  const counsellingNotes = Array.isArray(grading.counsellingNotes) ? grading.counsellingNotes : [];
  const studentCounselling = grading.studentCounselling || '';
  const unmatched = Array.isArray(grading.unmatchedSubmissions) ? grading.unmatchedSubmissions : [];
  const customMustHaves = Array.isArray(session.snapshot?.customMustHaves) ? session.snapshot.customMustHaves : [];
  const evaluatedRules = (Array.isArray(grading.ruleResults) ? grading.ruleResults : [])
    .filter((r) => r.passed === true || r.passed === false);

  return (
    <>
      <h5 className="mt-3">Per-prescription breakdown</h5>
      {slotResults.length === 0 ? (
        <Alert variant="info" className="small">No slots in this case.</Alert>
      ) : slotResults.map((slot, i) => (
        <Card key={slot.slotId || i} className={`mb-2 ${slot.matched ? 'border-success' : 'border-warning'}`}>
          <Card.Body className="py-2 px-3">
            <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-1">
              <div>
                <strong>{slot.slotLabel || `Prescription ${i + 1}`}</strong>
                {slot.controlledDrug ? <Badge bg="warning" text="dark" className="ms-2">CD</Badge> : null}
              </div>
              {slot.matched ? <Badge bg="success">Matched</Badge> : <Badge bg="warning" text="dark">Missed</Badge>}
            </div>
            {slot.matched ? (
              <Row className="g-2">
                <Col md={6}>
                  <div className="small text-muted mb-1">Student prescribed</div>
                  <PrescriptionDetail rx={slot.submitted || { drug: slot.submittedDrug }} />
                </Col>
                <Col md={6}>
                  <div className="small text-muted mb-1">Matched against</div>
                  <PrescriptionDetail rx={slot.matchedExpected || { drug: slot.matchedExpectedDrug }} />
                </Col>
                {slot.fields ? (
                  <Col xs={12} className="mt-1">
                    {Object.entries(slot.fields).map(([k, v]) => (
                      <Badge key={k} bg={v ? 'success' : 'danger'} className="me-1 mb-1">
                        {k}: {v ? '✓' : '✗'}
                      </Badge>
                    ))}
                  </Col>
                ) : null}
              </Row>
            ) : (
              <div className="mt-2">
                <div className="small text-muted mb-1">No matching drug submitted. Acceptable answers:</div>
                {(slot.acceptableAnswers || []).map((a, ai) => (
                  <div key={ai} className="border rounded p-2 mb-1" style={{ backgroundColor: '#f8f9fa' }}>
                    <PrescriptionDetail rx={a} />
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      ))}

      {unmatched.length > 0 ? (
        <Alert variant="warning">
          <strong>Unnecessary prescriptions:</strong> {unmatched.join(', ')}
        </Alert>
      ) : null}

      {evaluatedRules.length > 0 ? (
        <>
          <h6>Legal / rule checks</h6>
          {evaluatedRules.map((r, i) => (
            <div key={i} className="small mb-1">
              <Badge bg={r.passed ? 'success' : 'danger'} className="me-2">{r.passed ? '✓' : '✗'}</Badge>
              {r.key.replace(/_/g, ' ')}
              {r.notes ? <span className="text-muted ms-1">({r.notes})</span> : null}
            </div>
          ))}
        </>
      ) : null}

      {studentCounselling ? (
        <>
          <h6 className="mt-3">Student&apos;s counselling notes</h6>
          <Card><Card.Body className="py-2 px-3 small" style={{ whiteSpace: 'pre-wrap' }}>
            {studentCounselling}
          </Card.Body></Card>
        </>
      ) : (
        <div className="small text-muted mt-3 fst-italic">Student didn&apos;t submit any counselling notes.</div>
      )}

      {counsellingNotes.length > 0 || customMustHaves.length > 0 ? (
        <>
          <h6 className="mt-3">Lecturer-authored counselling feedback (auto-shown to student)</h6>
          {counsellingNotes.map((note, i) => (
            <Card key={`cf-${i}`} className="mb-2"><Card.Body className="py-2 px-3 small">
              <div className="fw-semibold">{note.slotLabel}{note.drug ? ` · ${note.drug}` : ''}</div>
              <div>{note.feedback}</div>
            </Card.Body></Card>
          ))}
          {customMustHaves.length > 0 ? (
            <Card><Card.Body className="py-2 px-3 small">
              <div className="fw-semibold">Exercise-level</div>
              <ul className="mb-0">{customMustHaves.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </Card.Body></Card>
          ) : null}
        </>
      ) : null}
    </>
  );
};

const VerifyBreakdown = ({ session }) => {
  const grading = session.grading || {};
  const flagResults = Array.isArray(grading.flagResults) ? grading.flagResults : [];
  const truePositives = flagResults.filter((r) => r.status === 'true_positive');
  const falsePositives = flagResults.filter((r) => r.status === 'false_positive' || r.status === 'invalid');
  const missed = Array.isArray(grading.missedErrors) ? grading.missedErrors : [];
  const vp = session.snapshot?.verifyPrescription;

  return (
    <>
      {vp ? (
        <>
          <h5 className="mt-3">Prescription the student reviewed</h5>
          <Card className="mb-3"><Card.Body className="py-2 px-3">
            {(vp.drugs || []).map((d, i) => (
              <div key={i} className="border rounded p-2 mb-2 small" style={{ backgroundColor: d.isControlledDrug ? '#fff8e1' : '#fafafa' }}>
                <div className="fw-semibold">
                  {d.drug || '—'}{d.strength ? ` ${d.strength}` : ''}{d.formulation ? ` (${d.formulation})` : ''}
                  {d.isControlledDrug ? <Badge bg="warning" text="dark" className="ms-2">CD</Badge> : null}
                </div>
                <div className="text-muted">
                  {d.doseAmount != null && d.doseAmount !== '' ? `${d.doseAmount}${d.doseUnit ? ` ${d.doseUnit}` : ''}` : ''}
                  {d.frequency ? ` · ${d.frequency}` : ''}
                  {d.route ? ` · ${d.route}` : ''}
                  {d.durationDays ? ` · ${d.durationDays} day${d.durationDays === 1 ? '' : 's'}` : ''}
                </div>
              </div>
            ))}
          </Card.Body></Card>
        </>
      ) : null}

      <h5>Action chosen</h5>
      <p className="small mb-2">
        Student picked: <strong>{verifyActionLabel(grading.submittedAction)}</strong>
        <br />
        Correct action: <strong>{verifyActionLabel(grading.correctAction)}</strong>
        {' '}
        {grading.actionCorrect
          ? <Badge bg="success">Correct</Badge>
          : <Badge bg="danger">Wrong</Badge>}
      </p>
      {grading.actionRationale ? (
        <Card className="mb-3 border-info">
          <Card.Body className="py-2 px-3">
            <div className="text-muted small text-uppercase mb-1">Student rationale</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{grading.actionRationale}</div>
          </Card.Body>
        </Card>
      ) : grading.submittedAction && grading.submittedAction !== 'sign_off' ? (
        <Alert variant="warning" className="small py-2 mb-3">
          No rationale captured. Older submissions may pre-date the requirement.
        </Alert>
      ) : null}

      {truePositives.length > 0 ? (
        <>
          <h6 className="mt-3">Correctly flagged</h6>
          {truePositives.map((r, i) => (
            <Card key={i} className="mb-2 border-success"><Card.Body className="py-2 px-3 small">
              <Badge {...SEVERITY_BADGE(r.severity)} className="me-2">{r.severity}</Badge>
              <span className="fw-semibold">{r.key.replace(/_/g, ' ')}</span>
              {r.drug ? <span className="text-muted"> · {r.drug}</span> : null}
              {r.note ? <div className="text-muted mt-1"><strong>Lecturer note:</strong> {r.note}</div> : null}
            </Card.Body></Card>
          ))}
        </>
      ) : null}

      {missed.length > 0 ? (
        <>
          <h6 className="mt-3">Missed errors</h6>
          {missed.map((m, i) => (
            <Card key={i} className="mb-2 border-warning"><Card.Body className="py-2 px-3 small">
              <Badge {...SEVERITY_BADGE(m.severity)} className="me-2">{m.severity}</Badge>
              <span className="fw-semibold">{m.label || m.key}</span>
              {m.drug ? <span className="text-muted"> · {m.drug}</span> : null}
              {m.note ? <div className="text-muted mt-1"><strong>Note:</strong> {m.note}</div> : null}
            </Card.Body></Card>
          ))}
        </>
      ) : null}

      {falsePositives.length > 0 ? (
        <>
          <h6 className="mt-3">Over-flagged (false positives)</h6>
          {falsePositives.map((r, i) => (
            <Alert key={i} variant="warning" className="py-2 px-3 small mb-2">
              {r.key?.replace(/_/g, ' ') || '(unknown)'}
              {r.drug ? ` · ${r.drug}` : ''}
            </Alert>
          ))}
        </>
      ) : null}
    </>
  );
};

const MODULE_LABEL = {
  case: 'Case study',
  drill: 'Calc drill',
  'spot-error': 'Spot-the-error',
  outpatient: 'Outpatient',
};

const ISSUE_TYPE_LABEL = ISSUE_TYPES.reduce((acc, t) => { acc[t.value] = t.label; return acc; }, {});
const issueLabel = (slug) => ISSUE_TYPE_LABEL[slug] || slug || '—';

const renderAnswerValue = (value) => {
  if (value === null || value === undefined || value === '') return <em className="text-muted">no answer</em>;
  if (Array.isArray(value)) {
    const joined = value.map((v) => String(v ?? '').trim()).filter(Boolean).join(', ');
    return joined || <em className="text-muted">no answer</em>;
  }
  if (typeof value === 'object') {
    const pairs = Object.entries(value)
      .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
      .map(([k, v]) => `${k}: ${v}`)
      .join(' | ');
    return pairs || <em className="text-muted">no answer</em>;
  }
  return String(value);
};

const CorrectnessBadge = ({ value }) => {
  if (value === true) return <Badge bg="success">Correct</Badge>;
  if (value === false) return <Badge bg="danger">Incorrect</Badge>;
  return <Badge bg="secondary">Manual review</Badge>;
};

// ===== Per-module breakdown components =====
//
// Each breakdown gets a `session` whose shape is whatever the server's
// /api/reviews/sessions/:moduleType/:sessionId endpoint returns for that
// moduleType. The server live-regrades on read so any rule changes since
// submit time are reflected.

const CaseBreakdown = ({ session }) => {
  const questions = Array.isArray(session.snapshot?.questions) ? session.snapshot.questions : [];
  const grading = session.grading || {};
  const breakdown = Array.isArray(grading.breakdown) ? grading.breakdown : [];
  const answers = session.answers || {};
  const reflection = session.progress?.reflection || '';

  if (!questions.length) {
    return <Alert variant="info" className="small">This case has no questions in its snapshot.</Alert>;
  }

  return (
    <>
      <h5 className="mt-3">Per-question breakdown</h5>
      {reflection ? (
        <Alert variant="light" className="mb-3">
          <strong>Learner reflection:</strong>
          <div style={{ whiteSpace: 'pre-wrap' }}>{reflection}</div>
        </Alert>
      ) : null}
      {questions.map((q) => {
        const row = breakdown.find((b) => String(b.questionNumber) === String(q.questionNumber));
        const submitted = answers[String(q.questionNumber)];
        const expected = row?.correctAnswer;
        return (
          <Card key={q.questionNumber} className="mb-2">
            <Card.Body className="py-2 px-3">
              <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-1">
                <div>
                  <div className="fw-semibold">Q{q.questionNumber}: {q.questionTitle}</div>
                  <div className="small text-muted">{q.questionText}</div>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <Badge bg="secondary">{q.questionType}</Badge>
                  <CorrectnessBadge value={row?.isCorrect} />
                </div>
              </div>
              <Row className="g-2 small mt-1">
                <Col md={6}>
                  <div className="text-muted">Submitted answer</div>
                  <div>{renderAnswerValue(submitted)}</div>
                </Col>
                {expected !== undefined && expected !== null ? (
                  <Col md={6}>
                    <div className="text-muted">Expected answer</div>
                    <div>{renderAnswerValue(expected)}</div>
                  </Col>
                ) : null}
              </Row>
              {q.answerExplanation ? (
                <Alert variant="info" className="mt-2 py-2 px-3 mb-0 small">
                  <strong>Explanation:</strong> {q.answerExplanation}
                </Alert>
              ) : null}
            </Card.Body>
          </Card>
        );
      })}
    </>
  );
};

const DrillBreakdown = ({ session }) => {
  const breakdown = Array.isArray(session.grading?.breakdown) ? session.grading.breakdown : [];

  if (!breakdown.length) {
    return <Alert variant="info" className="small">No questions in this drill's snapshot.</Alert>;
  }

  return (
    <>
      <h5 className="mt-3">Per-question breakdown</h5>
      {breakdown.map((row, i) => (
        <Card key={row.questionId || i} className={`mb-2 ${row.isCorrect ? 'border-success' : 'border-danger'}`}>
          <Card.Body className="py-2 px-3">
            <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-1">
              <div>
                <div className="small text-muted">Question {i + 1}{row.templateType === 'mcq' ? ' · multiple choice' : ' · calculation'}</div>
                <div>{row.prompt}</div>
              </div>
              <CorrectnessBadge value={row.isCorrect} />
            </div>
            <Row className="g-2 small mt-1">
              <Col md={6}>
                <div className="text-muted">Submitted</div>
                <div>
                  <strong>
                    {row.submittedAnswer == null || row.submittedAnswer === ''
                      ? <em className="text-muted">no answer</em>
                      : String(row.submittedAnswer)}
                  </strong>
                  {row.unit ? <span className="text-muted ms-1">{row.unit}</span> : null}
                </div>
              </Col>
              <Col md={6}>
                <div className="text-muted">Expected</div>
                <div>
                  <strong>{row.expected == null ? '—' : String(row.expected)}</strong>
                  {row.unit ? <span className="text-muted ms-1">{row.unit}</span> : null}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ))}
    </>
  );
};

const ScorePill = ({ label, value, suffix = '%' }) => (
  <Card style={{ minWidth: 130 }} className="flex-grow-1">
    <Card.Body className="text-center py-2 px-2">
      <div className="text-muted small">{label}</div>
      <div className="h4 fw-semibold mb-0">{value}{suffix}</div>
    </Card.Body>
  </Card>
);

const SpotErrorBreakdown = ({ session }) => {
  const grading = session.grading || {};
  const scenario = session.snapshot?.scenario || {};
  const prescriptions = Array.isArray(scenario.prescriptionList) ? scenario.prescriptionList : [];
  const expectedIssues = Array.isArray(session.snapshot?.expectedIssues) ? session.snapshot.expectedIssues : [];
  const flags = Array.isArray(session.flags) ? session.flags : [];
  const flagByDrug = new Map(flags.map((f) => [String(f.drug || '').toLowerCase(), f]));
  const expectedByDrug = new Map(expectedIssues.map((e) => [String(e.drug || '').toLowerCase(), e]));

  return (
    <>
      <h5 className="mt-3">Spot-the-error result</h5>
      <div className="d-flex gap-2 flex-wrap mb-3">
        <ScorePill label="Caught" value={`${grading.truePositives ?? 0}/${expectedIssues.length}`} suffix="" />
        <ScorePill label="Recall" value={Math.round(grading.recallPct ?? 0)} />
        <ScorePill label="Precision" value={Math.round(grading.precisionPct ?? 0)} />
        <ScorePill label="F1" value={Math.round(grading.f1Pct ?? 0)} />
        {session.durationSeconds != null ? (
          <ScorePill label="Time" value={session.durationSeconds} suffix="s" />
        ) : null}
      </div>

      {prescriptions.length > 0 ? (
        <>
          <h6>Chart the student saw</h6>
          <Card className="mb-3"><Card.Body className="py-2 px-3">
            {prescriptions.map((rx, i) => {
              const drugKey = String(rx.drug || '').toLowerCase();
              const flagged = flagByDrug.get(drugKey);
              const expected = expectedByDrug.get(drugKey);
              let badge = null;
              if (flagged && expected && flagged.issueType === expected.issueType) {
                badge = <Badge bg="success">Correctly flagged · {issueLabel(flagged.issueType)}</Badge>;
              } else if (flagged && expected) {
                badge = <Badge bg="warning" text="dark">Wrong category · said {issueLabel(flagged.issueType)} · expected {issueLabel(expected.issueType)}</Badge>;
              } else if (flagged && !expected) {
                badge = <Badge bg="warning" text="dark">Over-flagged · {issueLabel(flagged.issueType)}</Badge>;
              } else if (!flagged && expected) {
                badge = <Badge bg="danger">Missed · {issueLabel(expected.issueType)}</Badge>;
              } else {
                badge = <Badge bg="secondary">No issue</Badge>;
              }
              return (
                <div key={i} className="d-flex justify-content-between align-items-start gap-2 py-1 border-bottom small">
                  <div>
                    <strong>{rx.drug}</strong>
                    <div className="text-muted">
                      {rx.dose ? `${rx.dose} · ` : ''}{rx.frequency || ''}{rx.route ? ` · ${rx.route}` : ''}
                    </div>
                  </div>
                  {badge}
                </div>
              );
            })}
          </Card.Body></Card>
        </>
      ) : null}

      {Array.isArray(grading.missed) && grading.missed.length > 0 ? (
        <>
          <h6>Missed errors (with lecturer explanation)</h6>
          {grading.missed.map((m, i) => (
            <Card key={i} className="mb-2 border-danger"><Card.Body className="py-2 px-3 small">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <strong>{m.drug}</strong>
                <Badge bg="danger">{issueLabel(m.issueType)}</Badge>
              </div>
              {m.explanation ? <div>{m.explanation}</div> : null}
            </Card.Body></Card>
          ))}
        </>
      ) : null}

      {Array.isArray(grading.wronglyFlagged) && grading.wronglyFlagged.length > 0 ? (
        <>
          <h6>Wrongly flagged / wrong category</h6>
          {grading.wronglyFlagged.map((w, i) => (
            <Card key={i} className="mb-2 border-warning"><Card.Body className="py-2 px-3 small">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <strong>{w.drug}</strong>
                <Badge bg="warning" text="dark">Student said: {issueLabel(w.issueType)}</Badge>
              </div>
            </Card.Body></Card>
          ))}
        </>
      ) : null}
    </>
  );
};

const renderModuleBreakdown = (moduleType, session) => {
  switch (moduleType) {
    case 'case': return <CaseBreakdown session={session} />;
    case 'drill': return <DrillBreakdown session={session} />;
    case 'spot-error': return <SpotErrorBreakdown session={session} />;
    default:
      return (
        <Alert variant="secondary" className="mb-3 small">
          Rich detail for {MODULE_LABEL[moduleType] || moduleType} sessions isn&apos;t available.
          The auto-graded score, student identity and timestamps are shown above.
        </Alert>
      );
  }
};

const FacilitatorReviewDetail = ({ token, moduleType, sessionId, onBack, onReviewed }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [overrideInput, setOverrideInput] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!token || !sessionId || !moduleType) return;
    try {
      setLoading(true);
      const result = await api.getReviewSession(moduleType, sessionId, token);
      setSession(result.session);
      setComment(result.session?.reviewComment || '');
      setOverrideInput(result.session?.scoreOverride == null ? '' : String(result.session.scoreOverride));
      setOverrideReason(result.session?.scoreOverrideReason || '');
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load this submission.');
    } finally {
      setLoading(false);
    }
  }, [token, moduleType, sessionId]);

  useEffect(() => { refresh(); }, [refresh]);

  const parseOverride = () => {
    const trimmed = overrideInput.trim();
    if (trimmed === '') return { ok: true, value: null };
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      return { ok: false, error: 'Adjusted score must be a number between 0 and 100.' };
    }
    return { ok: true, value: Math.round(n * 100) / 100 };
  };

  const handleMarkReviewed = async () => {
    setSaving(true);
    try {
      const supportsOverride = moduleType === 'outpatient';
      const args = { comment };
      if (supportsOverride) {
        const parsed = parseOverride();
        if (!parsed.ok) { setError(parsed.error); setSaving(false); return; }
        args.scoreOverride = parsed.value;
        args.scoreOverrideReason = overrideReason;
      }
      await api.markSessionReviewed(moduleType, sessionId, args, token);
      await refresh();
      onReviewed?.();
    } catch (err) {
      setError(err?.message || 'Could not save the review.');
    } finally {
      setSaving(false);
    }
  };

  const handleClearOverride = async () => {
    setSaving(true);
    try {
      await api.markSessionReviewed(
        moduleType,
        sessionId,
        { comment, scoreOverride: null, scoreOverrideReason: null },
        token
      );
      await refresh();
      onReviewed?.();
    } catch (err) {
      setError(err?.message || 'Could not clear the adjustment.');
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    setSaving(true);
    try {
      await api.reopenReviewSession(moduleType, sessionId, token);
      await refresh();
      onReviewed?.();
    } catch (err) {
      setError(err?.message || 'Could not reopen the session.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="student-page"><Container className="mt-4"><Spinner animation="border" /> Loading…</Container></div>;
  }

  if (!session) {
    return (
      <div className="student-page">
        <Container className="mt-4">
          {error ? <Alert variant="danger">{error}</Alert> : <Alert variant="info">Session not found.</Alert>}
          <Button variant="outline-secondary" onClick={onBack}>Back to queue</Button>
        </Container>
      </div>
    );
  }

  const isOutpatient = moduleType === 'outpatient';
  const isVerify = isOutpatient && session.drillMode === 'verify';
  const reviewed = Boolean(session.reviewedAt);
  const supportsOverride = isOutpatient;

  return (
    <div className="student-page">
      <Container className="mt-4 mb-5">
        <FacilitatorPageHeader
          title={<>
            {session.drillTitle || 'Submission review'}
            {isOutpatient ? (
              isVerify
                ? <Badge bg="info" className="ms-2"><i className="bi bi-search me-1" />Verify</Badge>
                : <Badge bg="primary" className="ms-2"><i className="bi bi-pencil-square me-1" />Prescribe</Badge>
            ) : (
              <Badge bg="secondary" className="ms-2">{MODULE_LABEL[moduleType] || moduleType}</Badge>
            )}
          </>}
          description={<>
            <strong>{session.studentName}</strong>{session.studentEmail ? ` (${session.studentEmail})` : ''}
            {' · '}attempt #{session.attemptNumber || 1}
            {' · '}submitted {formatDate(session.completedAt)}
          </>}
          onBack={onBack}
          backLabel="Back to queue"
        />

        {error ? <Alert variant="danger">{error}</Alert> : null}

        <div className="d-flex flex-wrap gap-3 mb-3">
          {isOutpatient ? (
            <>
              <Card className="flex-grow-1" style={{ minWidth: 180 }}>
                <Card.Body className="text-center py-3">
                  <div className="text-muted small">{isVerify ? 'Flag F1' : 'Drug score'}</div>
                  <div className="display-6 fw-semibold">{Math.round(session.fieldScorePct ?? 0)}%</div>
                </Card.Body>
              </Card>
              <Card className="flex-grow-1" style={{ minWidth: 180 }}>
                <Card.Body className="text-center py-3">
                  <div className="text-muted small">{isVerify ? 'Action' : 'Legal / rule score'}</div>
                  <div className="display-6 fw-semibold">
                    {isVerify
                      ? (session.grading?.actionCorrect ? '✓' : '✗')
                      : `${Math.round(session.ruleScorePct ?? 0)}%`}
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : null}
          <Card className="flex-grow-1" style={{ minWidth: 180 }}>
            <Card.Body className="text-center py-3">
              <div className="text-muted small">
                {session.scoreOverride == null ? 'Overall' : 'Effective (adjusted)'}
              </div>
              <div className="display-6 fw-semibold">
                {Math.round((session.effectiveScorePct ?? session.overallPct) ?? 0)}%
              </div>
              {session.scoreOverride != null ? (
                <div className="small text-muted mt-1">
                  Auto-grade: {Math.round(session.overallPct ?? 0)}%
                </div>
              ) : null}
            </Card.Body>
          </Card>
        </div>

        {session.scoreOverride != null && session.scoreOverrideReason ? (
          <Alert variant="info" className="small mb-3">
            <strong>Adjustment reason:</strong> {session.scoreOverrideReason}
          </Alert>
        ) : null}

        {isOutpatient && session.snapshot?.scenario ? (
          <Alert variant="primary" className="mb-3">
            <h6 className="text-uppercase small fw-bold mb-2">Clinical scenario the student saw</h6>
            <div style={{ whiteSpace: 'pre-wrap' }}>{session.snapshot.scenario}</div>
          </Alert>
        ) : null}

        {isOutpatient && session.snapshot?.patient ? (
          <Card className="mb-3"><Card.Body className="py-3">
            <h6 className="text-uppercase small text-muted mb-2">Patient</h6>
            <Row className="g-2 small">
              <Col md={6}><strong>Name:</strong> {session.snapshot.patient.name || '—'}</Col>
              <Col md={3}><strong>DOB:</strong> {session.snapshot.patient.dob || '—'}</Col>
              <Col md={3}><strong>Allergies:</strong> {session.snapshot.patient.allergies || 'NKDA'}</Col>
            </Row>
          </Card.Body></Card>
        ) : null}

        {isOutpatient
          ? (isVerify ? <VerifyBreakdown session={session} /> : <PrescribeBreakdown session={session} />)
          : renderModuleBreakdown(moduleType, session)}

        <Card className="mt-4 border-primary">
          <Card.Body>
            <h5 className="mb-2">Lecturer review</h5>
            {reviewed ? (
              <Alert variant="success" className="small mb-2">
                Reviewed {formatDate(session.reviewedAt)}
                {session.reviewerName ? ` by ${session.reviewerName}` : ''}.
              </Alert>
            ) : null}
            <Form.Group className="mb-3">
              <Form.Label>Comment for the student / record</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Optional. e.g. Counselling on adherence was solid; the choice of route would have unsettled the patient — let&apos;s talk about it next session."
                disabled={saving}
              />
            </Form.Group>

            {supportsOverride ? (
            <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
              <Card.Body className="py-3 px-3">
                <h6 className="mb-1">Adjust score (optional)</h6>
                <div className="text-muted small mb-2">
                  Override the auto-graded overall score when counselling notes or judgement calls deserve credit
                  the engine can&apos;t award. Leave blank to keep the auto-grade.
                </div>
                <Row className="g-2 align-items-end">
                  <Col xs={6} md={3}>
                    <Form.Label className="small mb-1">Adjusted score (%)</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={100}
                      step="0.5"
                      value={overrideInput}
                      onChange={(e) => setOverrideInput(e.target.value)}
                      placeholder={String(Math.round(session.overallPct ?? 0))}
                      disabled={saving}
                    />
                  </Col>
                  <Col xs={12} md={9}>
                    <Form.Label className="small mb-1">Reason (visible alongside the adjustment)</Form.Label>
                    <Form.Control
                      type="text"
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="e.g. Counselling notes covered safety-netting clearly; credited."
                      disabled={saving || overrideInput.trim() === ''}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            ) : null}

            <div className="d-flex gap-2 flex-wrap lecturer-review-actions">
              <Button variant="primary" onClick={handleMarkReviewed} disabled={saving}>
                {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : (reviewed ? 'Save changes' : 'Mark as reviewed')}
              </Button>
              {supportsOverride && session.scoreOverride != null ? (
                <Button variant="outline-dark" onClick={handleClearOverride} disabled={saving}>
                  Clear adjustment
                </Button>
              ) : null}
              {reviewed ? (
                <Button variant="outline-dark" onClick={handleReopen} disabled={saving}>
                  Reopen for review
                </Button>
              ) : null}
              <Button variant="outline-secondary" onClick={onBack} disabled={saving}>Back</Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default FacilitatorReviewDetail;
